import React, { useState, useEffect } from "react";
import {
  Paper,
  Container,
  Typography,
  Box,
  Grid,
  TextField,
  MenuItem,
  CircularProgress
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { es } from "date-fns/locale";
import { subDays, startOfDay, endOfDay, format, parse } from "date-fns";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import MetricsCards from "../../components/CampaignShippingReports/MetricsCards";
import EstimationsPanel from "../../components/CampaignShippingReports/EstimationsPanel";
import ShippingDetailTable from "../../components/CampaignShippingReports/ShippingDetailTable";
import ChartsSection from "../../components/CampaignShippingReports/ChartsSection";

// Clase personalizada de DateFnsUtils para evitar error de formato en VPS
// FIX: Soluciona el error "Format string contains an unescaped latin alphabet character 'n'"
// que ocurre en algunos VPS cuando Material-UI Pickers intenta formatear/parsear fechas
class CustomDateFnsUtils extends DateFnsUtils {
  // Formato seguro siempre: usar formato con caracteres escapados
  // Esto evita problemas con date-fns en diferentes versiones/entornos
  static SAFE_FORMAT = "dd'/'MM'/'yyyy";

  format(date, formatString) {
    if (!date) return "";
    try {
      // Siempre usar formato seguro con caracteres escapados
      // Ignorar formatString para evitar problemas de parsing
      return format(date, CustomDateFnsUtils.SAFE_FORMAT, { locale: es });
    } catch (error) {
      console.error("Error formateando fecha:", error);
      // Fallback: método original (puede fallar pero al menos intenta)
      try {
        return super.format(date, formatString);
      } catch (superError) {
        // Último fallback: formato simple sin locale
        return format(date, CustomDateFnsUtils.SAFE_FORMAT);
      }
    }
  }

  parse(value, formatString) {
    if (!value || typeof value !== "string") return null;
    try {
      // Siempre usar formato seguro para parsear
      // Ignorar formatString para evitar problemas de parsing
      return parse(value, CustomDateFnsUtils.SAFE_FORMAT, new Date(), { locale: es });
    } catch (error) {
      console.error("Error parseando fecha:", error);
      // Fallback: método original
      try {
        return super.parse(value, formatString);
      } catch (superError) {
        // Último fallback: intentar parsear sin locale
        try {
          return parse(value, CustomDateFnsUtils.SAFE_FORMAT, new Date());
        } catch (finalError) {
          return null;
        }
      }
    }
  }
}

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
    minHeight: "100vh"
  },
  mainPaper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    backgroundColor: theme.palette.background.paper
  },
  header: {
    marginBottom: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },
  title: {
    fontWeight: 600,
    color: theme.palette.primary.main
  },
  filtersContainer: {
    marginBottom: theme.spacing(3)
  },
  dateField: {
    marginRight: theme.spacing(2)
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px"
  }
}));

const CampaignShippingReports = () => {
  const classes = useStyles();

  // Estados para filtros
  const [startDate, setStartDate] = useState(startOfDay(subDays(new Date(), 7)));
  const [endDate, setEndDate] = useState(endOfDay(new Date()));
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [campaigns, setCampaigns] = useState([]);

  // Estados para datos
  const [metrics, setMetrics] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(true);

  // Cargar campañas disponibles
  useEffect(() => {
    const loadCampaigns = async () => {
      try {
        const { data } = await api.get("/campaigns", {
          params: { pageNumber: 1, limit: 1000 }
        });
        setCampaigns(data.records || []);
      } catch (err) {
        console.error("Error loading campaigns:", err);
        toastError(err);
      }
    };

    loadCampaigns();
  }, []);

  // Cargar métricas cuando cambien los filtros
  useEffect(() => {
    const loadMetrics = async () => {
      setLoadingMetrics(true);
      try {
        const params = {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        };

        if (selectedCampaignId) {
          params.campaignId = selectedCampaignId;
        }

        const { data } = await api.get("/campaign-shipping-reports/metrics", {
          params
        });

        setMetrics(data);
      } catch (err) {
        console.error("Error loading metrics:", err);
        toastError(err);
      } finally {
        setLoadingMetrics(false);
      }
    };

    loadMetrics();
  }, [startDate, endDate, selectedCampaignId]);

  return (
    <MuiPickersUtilsProvider utils={CustomDateFnsUtils} locale={es}>
      <Container maxWidth={false} className={classes.root}>
        <Paper className={classes.mainPaper}>
          <Box className={classes.header}>
            <Typography variant="h4" className={classes.title}>
              <span role="img" aria-label="gráfico">📊</span> Informe de Envío
            </Typography>
          </Box>

          {/* Filtros */}
          <Box className={classes.filtersContainer}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Fecha Inicio"
                  value={startDate}
                  onChange={setStartDate}
                  format="dd/MM/yyyy"
                  fullWidth
                  inputVariant="outlined"
                  className={classes.dateField}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Fecha Fin"
                  value={endDate}
                  onChange={setEndDate}
                  format="dd/MM/yyyy"
                  fullWidth
                  inputVariant="outlined"
                  className={classes.dateField}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label="Campaña"
                  value={selectedCampaignId}
                  onChange={(e) => setSelectedCampaignId(e.target.value)}
                  fullWidth
                  variant="outlined"
                >
                  <MenuItem value="">Todas las campañas</MenuItem>
                  {campaigns.map((campaign) => (
                    <MenuItem key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </Box>

          {/* Métricas */}
          {loadingMetrics ? (
            <Box className={classes.loadingContainer}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <MetricsCards metrics={metrics} />
              <EstimationsPanel
                campaigns={campaigns}
                selectedCampaignId={selectedCampaignId}
                setSelectedCampaignId={setSelectedCampaignId}
              />
              <ChartsSection
                metrics={metrics}
                startDate={startDate}
                endDate={endDate}
                selectedCampaignId={selectedCampaignId}
              />
              <ShippingDetailTable
                startDate={startDate}
                endDate={endDate}
                selectedCampaignId={selectedCampaignId}
              />
            </>
          )}
        </Paper>
      </Container>
    </MuiPickersUtilsProvider>
  );
};

export default CampaignShippingReports;

